import type { PricingDTOTypes } from '../types';

const DEFAULT_PRICING_OPTION: PricingDTOTypes.PricingOptionDTO = {
  numberOfPortions: 4,
  numberOfRecipes: 5,
  pricePerBox: '€79.80',
  firstBoxPricePerBox: '€39.90',
  pricePerPortion: '€3.99',
  firstBoxPricePerPortion: '€2.00',
  isBestValue: true,
};

export class CalculatePriceResponseDTOBuilder {
  private recipeKitPriceBeforeDiscount = '€12.00';
  private recipeKitTotalPrice: `€${string}` = '€5.20';
  private discountPercentage: `${string}%` = '60%';
  private hasDiscount: boolean = false;

  public withDiscountPercentage(discountPercentage: `${string}%`): this {
    this.discountPercentage = discountPercentage;
    return this;
  }

  public withRecipeKitPriceBeforeDiscount(price: `€${string}`): this {
    this.recipeKitPriceBeforeDiscount = price;
    return this;
  }

  public withRecipeKitTotalPrice(price: `€${string}`): this {
    this.recipeKitTotalPrice = price;
    return this;
  }

  public build(): PricingDTOTypes.GetCalculatePrice200DTO {
    const recipeKitTotalPrice = this.recipeKitTotalPrice;
    const discountPercent = this.discountPercentage;
    const hasNoDiscount = this.discountPercentage === '0%';
    const discountAmount = hasNoDiscount ? '−€0.00' : '−€6.80';

    return {
      wholeDelivery: {
        totalPrice: recipeKitTotalPrice,
        priceBeforeDiscount: this.recipeKitPriceBeforeDiscount,
        discountAmount,
        deliveryPrice: 'FREE',
        deliveryPriceBeforeDiscount: '€4.99',
      },
      recipeKit: {
        totalPrice: recipeKitTotalPrice,
        priceBeforeDiscount: this.recipeKitPriceBeforeDiscount,
        discountAmount,
      },
      recipeKitPerPortion: {
        totalPrice: '€1.30',
        priceBeforeDiscount: '€3.00',
        discountAmount: 'Not implemented',
      },
      discountPercent: discountPercent,
      hasDiscount: !hasNoDiscount,
    };
  }
}

export class PricingResponseDTOBuilder {
  private deliveryPrice: string = '€6.00';
  private discountAmount: string = '-€6.80';
  private recipeKitPriceBeforeDiscount = '€12.00';
  private discountPercentage: `${string}%` = '60%';
  private recipeKitTotalPrice: `€${string}` = '€5.20';

  public withFreeDelivery(): this {
    this.deliveryPrice = 'FREE';
    return this;
  }

  public withDiscountAmount(discountAmount: string): this {
    this.discountAmount = discountAmount;
    return this;
  }

  public withNoDiscount(): this {
    this.discountAmount = '-€0.00';
    return this;
  }

  public withDiscountPercentage(discountPercentage: `${string}%`): this {
    this.discountPercentage = discountPercentage;
    return this;
  }

  public withRecipeKitPriceBeforeDiscount(price: `€${string}`): this {
    this.recipeKitPriceBeforeDiscount = price;
    return this;
  }

  public withRecipeKitTotalPrice(price: `€${string}`): this {
    this.recipeKitTotalPrice = price;
    return this;
  }

  public build(): PricingDTOTypes.GetPredictPricing200DTO {
    const recipeKitPerPortionPriceBeforeDiscount = '€3.00';

    const isFreeDelivery = this.deliveryPrice === 'FREE';
    const hasNoDiscount = this.discountAmount === '-€0.00';

    const priceBeforeDiscount = isFreeDelivery ? this.recipeKitPriceBeforeDiscount : '€18.00';
    const recipeKitTotalPrice = hasNoDiscount
      ? this.recipeKitPriceBeforeDiscount
      : this.recipeKitTotalPrice;
    const totalIfDiscount = isFreeDelivery ? '€5.20' : '€11.20';
    const totalPrice = hasNoDiscount ? priceBeforeDiscount : totalIfDiscount;

    const recipeKitPerPortionTotalPrice = hasNoDiscount
      ? recipeKitPerPortionPriceBeforeDiscount
      : '€1.30';
    const discountPercent = hasNoDiscount ? '0%' : this.discountPercentage;

    return {
      wholeDelivery: {
        totalPrice,
        priceBeforeDiscount,
        discountAmount: this.discountAmount,
        deliveryPrice: this.deliveryPrice,
        deliveryPriceBeforeDiscount: '€4.99',
      },
      recipeKit: {
        totalPrice: recipeKitTotalPrice,
        priceBeforeDiscount: this.recipeKitPriceBeforeDiscount,
        discountAmount: this.discountAmount,
      },
      recipeKitPerPortion: {
        totalPrice: recipeKitPerPortionTotalPrice,
        priceBeforeDiscount: recipeKitPerPortionPriceBeforeDiscount,
        discountAmount: 'Not implemented',
      },
      discountPercent,
      hasDiscount: !hasNoDiscount,
    };
  }
}

export class PricingOptionsResponseDTOBuilder {
  private options: PricingDTOTypes.PricingOptionDTO[] = [];
  private discountSteps: PricingDTOTypes.DiscountStepDTO[] = [
    { kind: 'box', percentage: '50%', fromBox: 1, toBox: 1 },
    { kind: 'box', percentage: '40%', fromBox: 2, toBox: 2 },
    { kind: 'box', percentage: '25%', fromBox: 3, toBox: 3 },
  ];

  public withOption(option: PricingDTOTypes.PricingOptionDTO): this {
    this.options = [...this.options, option];
    return this;
  }

  public withOptions(options: PricingDTOTypes.PricingOptionDTO[]): this {
    this.options = options;
    return this;
  }

  public withDiscountSteps(discountSteps: PricingDTOTypes.DiscountStepDTO[]): this {
    this.discountSteps = discountSteps;
    return this;
  }

  public build(): PricingDTOTypes.PricingOptionsResponseData {
    return {
      options: this.options.length > 0 ? this.options : [DEFAULT_PRICING_OPTION],
      discountSteps: this.discountSteps,
    };
  }
}
